<script setup>
import { Navigation, Pagination } from "swiper/modules";
import { ref, watch, nextTick } from "vue";

const modules = [Navigation, Pagination];

const props = defineProps({
  images: {
    type: Array,
    default: [],
  },
  name: {
    type: String,
    default: "noname",
  },
});

const mainSwiper = ref();
const thumbsSwiper = ref();
const slideId = ref(0);

// Landscape sources cover the well (fills the UI); portrait packshots stay contained.
const landscape = ref({});
const onImgLoad = (i, e) => {
  const img = e?.target;
  if (img?.naturalWidth && img?.naturalHeight) {
    landscape.value = { ...landscape.value, [i]: img.naturalWidth > img.naturalHeight };
  }
};

const { getImgUrl: buildImgUrl } = useProductImage();
// Main/selected image: no padding — show it at its natural aspect (scale-down).
const getImgUrl = (img, h = 300) =>
  buildImgUrl(img, { width: Math.round((h * 2) / 3), height: h, quality: 80, fit: "scale-down" });
// Thumbnails: fit=pad in a square box so the nav strip stays uniform and both
// portrait packshots and landscape screenshots read well at small size.
const getThumbImgUrl = (img, size = 128) =>
  buildImgUrl(img, { width: size, height: size, quality: 80 });

const slideTo = (index, Sto = false) => {
  slideId.value = props.images.length > index ? index : 0;
  if (mainSwiper.value && Sto) {
    mainSwiper.value.swiper.slideTo(slideId.value);
  }
};

const onSlideChange = (e) => {
  slideTo(e.activeIndex);
};

watch(
  () => props.images,
  async (newVal) => {
    if (!newVal || !newVal.length) return;
    await nextTick();

    // only update the swipers in THIS component, not the whole document
    if (mainSwiper.value?.swiper) {
      mainSwiper.value.swiper.update();
    }
    if (thumbsSwiper.value?.swiper) {
      thumbsSwiper.value.swiper.update();
    }
  },
);
</script>

<template>
  <div class="w-full">
    <div v-if="images.length">
      <!-- Main Swiper -->
      <swiper-container ref="mainSwiper" :slides-per-view="1" :space-between="20" @slideChange="onSlideChange"
        class="swiper-wrapper h-auto rounded-2xl bg-white border border-gray-100" style="
          --swiper-theme-color: var(--color-primary);
          --swiper-navigation-color: var(--color-primary);
          --swiper-pagination-color: var(--color-primary);
        ">
        <swiper-slide v-for="(mainImg, i) in images" :key="i" class="p-2">
          <NuxtImg :src="getImgUrl(mainImg, 900)"
            class="w-full h-[460px] sm:h-[560px] rounded-xl transition-transform duration-500 hover:scale-[1.03]"
            :class="landscape[i] ? 'object-cover' : 'object-contain'"
            :alt="`${name} — ${i + 1}`" format="webp" :preload="i === 0" height="900"
            :loading="i === 0 ? 'eager' : 'lazy'" :fetch-priority="i === 0 ? 'high' : 'auto'"
            @load="onImgLoad(i, $event)" />
        </swiper-slide>
      </swiper-container>

      <!-- Clickable dots: one per image, comfortable hit target (custom because the
           swiper web component's built-in pagination doesn't initialize under Vue) -->
      <div v-if="images.length > 1" class="flex items-center justify-center gap-1 my-3" role="tablist"
        :aria-label="`${name} images`">
        <button v-for="(img, i) in images" :key="'dot-' + i" type="button"
          class="p-1 border-0 bg-transparent cursor-pointer group" :aria-label="`Go to image ${i + 1}`"
          @click="slideTo(i, true)">
          <span class="block rounded-full bg-[var(--color-primary)] transition-all duration-200"
            :class="slideId == i ? 'w-6 h-3' : 'w-3 h-3 opacity-35 group-hover:opacity-70'"></span>
        </button>
      </div>

      <ClientOnly>
        <!-- Thumbnail strip: only when there is something to pick between -->
        <div v-if="images.length > 1" class="flex flex-wrap">
          <div class="w-full relative">
            <swiper-container ref="thumbsSwiper" :modules="modules" :space-between="10" :grab-cursor="true"
              navigation="true" :slides-per-view="5" :breakpoints="{
                320: { slidesPerView: 5, spaceBetween: 8 },
                640: { slidesPerView: 6, spaceBetween: 10 },
                991: { slidesPerView: 7, spaceBetween: 10 }
              }" class="swiper-wrapper sw-fc" style="
              --swiper-theme-color: var(--color-primary);
              --swiper-navigation-color: var(--color-primary);
              --swiper-pagination-color: var(--color-primary);
            ">
              <swiper-slide v-for="(image, index) in images" :key="index" class="cursor-pointer"
                @click="slideTo(index, true)">
                <!-- Uniform square wells so the strip lines up regardless of source aspect -->
                <div
                  class="aspect-square w-full rounded-xl overflow-hidden bg-white border-2 transition-all duration-200"
                  :class="slideId == index
                    ? 'border-[var(--color-primary)] shadow-sm'
                    : 'border-gray-100 opacity-75 hover:opacity-100 hover:border-gray-300'">
                  <NuxtImg :src="getThumbImgUrl(image, 160)" class="w-full h-full object-contain p-1"
                    format="webp" loading="lazy" fetch-priority="low" width="160" height="160"
                    :alt="`${name} thumbnail ${index + 1}`" />
                </div>
              </swiper-slide>
            </swiper-container>
          </div>
        </div>
      </ClientOnly>
    </div>

    <div v-else>
      <div class="w-full rounded-2xl bg-white border border-gray-100 overflow-hidden p-2">
        <NuxtImg :alt="name"
          src="https://assets.cld.be/cdn-cgi/image/width=900,quality=40/9/3/1/0/931054989870582dda8788cf81759668c311beee_Image_Coming_Soon.png"
          class="w-full h-[460px] sm:h-[560px] object-contain" format="webp" preload loading="eager"
          fetch-priority="high" height="900" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Swiper hover effects */
.swiper-button-next:hover,
.swiper-button-prev:hover {
  filter: brightness(0.9);
}
</style>
